import { AuthChangeEvent } from '@supabase/supabase-js';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { ReactNode, useEffect, useRef } from 'react';
import { createStore, useStore } from 'zustand';

import {
  getAccessTokenForProvider,
  getAuthProvider,
  getStoredAuthProvider,
  setStoredAuthProvider,
} from '../providers/auth-provider-registry';
import { supabaseAuthProvider } from '../providers/supabase-auth-provider';
import { AuthProviderType } from '../types/enums/auth-provider-type';
import { AuthSession, AuthUser } from '../types/interfaces/auth-session';
import { SupabaseProviderConfig } from '../types/supabase';

import supabaseProviderService from '@/features/auth/services/supabase-provider.service';
import { StorageKeys, StorageService } from '@/services/storage.service';
import { useUserStore } from '@/store/user-store';

interface AuthStoreState {
  session: AuthSession | null;
  loading: boolean;
  activeProvider: AuthProviderType | null;
  setSession: (session: AuthSession | null) => void;
  setLoading: (loading: boolean) => void;
  setActiveProvider: (provider: AuthProviderType | null) => void;
}

export interface AuthContextValue extends AuthStoreState {
  isAuthenticated: boolean;
  loginWithSupabase: (email: string, password: string) => Promise<void>;
  loginWithSupabaseSSO: (providerId: string, redirectTo: string) => Promise<string | null>;
  resolveSupabaseProviderForLoginEmail: (email: string) => SupabaseProviderConfig;
  loginWithAuth0: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

export const authStore = createStore<AuthStoreState>(set => ({
  session: null,
  loading: true,
  activeProvider: getStoredAuthProvider(),
  setSession: session => {
    set({ session });
  },
  setLoading: loading => {
    set({ loading });
  },
  setActiveProvider: provider => {
    setStoredAuthProvider(provider);
    set({ activeProvider: provider });
  },
}));

const CHANGE_PASSWORD_PATHNAME = '/settings/change-password';

const EXCLUDED_ROUTES_FROM_NAVIGATING_TO_SET_UP_PASSWORD_SCREEN = [
  '/set-up-password',
  '/forgot-password',
  '/reset-password',
];

const normalizeReturnPath = (path: string | undefined) => {
  if (!path || !path.startsWith('/')) {
    return '/';
  }

  return path;
};

const clearPersistedUserAuthData = () => {
  const userStore = useUserStore.getState();

  userStore.clearOrganizationToken();
  userStore.clearSiteToken();
  userStore.clearUserId();
  userStore.clearUserRole();
  userStore.clearIsSSO();

  StorageService.removeItem(StorageKeys.USER_ID);
  StorageService.removeItem(StorageKeys.USER_ROLE);
  StorageService.removeItem(StorageKeys.ORGANIZATION_TOKEN);
  StorageService.removeItem(StorageKeys.SITE_TOKEN);
  supabaseProviderService.clearPersistedProvider();
  setStoredAuthProvider(null);
};

const handleSupabaseAuthEvent = (
  event: AuthChangeEvent,
  session: AuthSession | null,
  pathname: string,
  setSession: (session: AuthSession | null) => void,
  navigate: ReturnType<typeof useNavigate>,
  setIsSSO: (isSSO: boolean) => void,
  clearIsSSO: () => void,
) => {
  if (event === 'INITIAL_SESSION') {
    if (session) {
      setIsSSO(supabaseProviderService.isSSO);
    } else {
      clearIsSSO();
    }
  }

  if (event === 'SIGNED_IN') {
    setIsSSO(Boolean(session) && supabaseProviderService.isSSO);
  }

  if (event === 'SIGNED_OUT') {
    clearIsSSO();
  }

  if (
    event === 'PASSWORD_RECOVERY' &&
    !EXCLUDED_ROUTES_FROM_NAVIGATING_TO_SET_UP_PASSWORD_SCREEN.includes(pathname)
  ) {
    navigate({ to: '/set-up-password' });
  }

  if (event === 'PASSWORD_RECOVERY' && pathname === '/forgot-password') {
    navigate({ to: '/reset-password' });
  }

  setSession(session);

  if (event === 'USER_UPDATED' && pathname !== CHANGE_PASSWORD_PATHNAME) {
    navigate({ to: '/' });
  }

  if (event === 'SIGNED_OUT') {
    navigate({ to: '/login' });
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const authStoreSetLoading = useStore(authStore, state => state.setLoading);
  const authStoreSetSession = useStore(authStore, state => state.setSession);
  const authStoreSetActiveProvider = useStore(authStore, state => state.setActiveProvider);
  const activeProvider = useStore(authStore, state => state.activeProvider);
  const setIsSSO = useUserStore(state => state.setIsSSO);
  const clearIsSSO = useUserStore(state => state.clearIsSSO);

  const navigate = useNavigate();
  const pathname = useLocation({ select: location => location.pathname });
  const pathnameRef = useRef(pathname);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    // Guards async auth initialization against updating state after unmount.
    let isMounted = true;
    // Holds Supabase auth listener cleanup when that provider is active.
    let unsubscribeSupabase: (() => void) | null = null;

    const initializeAuth = async () => {
      authStoreSetLoading(true);

      try {
        // Explicit provider path: initialize only the selected provider.
        if (activeProvider === AuthProviderType.SUPABASE) {
          const session = await supabaseAuthProvider.getSession();

          if (!isMounted) {
            return;
          }

          authStoreSetSession(session);

          unsubscribeSupabase = supabaseAuthProvider.onAuthStateChange((event, updatedSession) => {
            handleSupabaseAuthEvent(
              event,
              updatedSession,
              pathnameRef.current,
              authStoreSetSession,
              navigate,
              setIsSSO,
              clearIsSSO,
            );
          });

          return;
        }

        if (activeProvider === AuthProviderType.AUTH0) {
          const auth0Provider = getAuthProvider(AuthProviderType.AUTH0);
          const redirectResult = await auth0Provider.handleRedirectCallback?.();
          const session = await auth0Provider.getSession();

          if (!isMounted) {
            return;
          }

          authStoreSetSession(session);
          clearIsSSO();

          if (redirectResult?.returnTo) {
            const normalizedPath = normalizeReturnPath(redirectResult.returnTo);
            const returnPath = normalizedPath === '/login' ? '/' : normalizedPath;
            window.location.replace(returnPath);
            return;
          }

          if (session && pathnameRef.current === '/login') {
            navigate({ to: '/' });
          }

          return;
        }

        // No provider persisted in storage:
        // first try Supabase session restoration, then fallback to Auth0.
        const supabaseSession = await supabaseAuthProvider.getSession().catch(() => null);

        if (!isMounted) {
          return;
        }

        if (supabaseSession) {
          authStoreSetActiveProvider(AuthProviderType.SUPABASE);
          authStoreSetSession(supabaseSession);
          return;
        }

        const auth0Provider = getAuthProvider(AuthProviderType.AUTH0);
        const redirectResult = await auth0Provider.handleRedirectCallback?.();
        const auth0Session = await auth0Provider.getSession();

        if (!isMounted) {
          return;
        }

        if (auth0Session) {
          authStoreSetActiveProvider(AuthProviderType.AUTH0);
          authStoreSetSession(auth0Session);
          clearIsSSO();

          if (redirectResult?.returnTo) {
            const normalizedPath = normalizeReturnPath(redirectResult.returnTo);
            const returnPath = normalizedPath === '/login' ? '/' : normalizedPath;
            window.location.replace(returnPath);
            return;
          }

          if (pathnameRef.current === '/login') {
            navigate({ to: '/' });
          }
        }
      } catch {
        if (isMounted) {
          authStoreSetSession(null);
        }
      } finally {
        if (isMounted) {
          authStoreSetLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      // Stop any post-await state updates and detach provider listeners.
      isMounted = false;
      unsubscribeSupabase?.();
    };
  }, [activeProvider, authStoreSetLoading, authStoreSetSession, clearIsSSO, navigate, setIsSSO]);

  return <>{children}</>;
};

export const authStoreOutsideComponent = () => {
  return authStore.getState();
};

export const getAccessTokenFromActiveProvider = async () => {
  const { activeProvider } = authStore.getState();

  if (!activeProvider) {
    return null;
  }

  return getAccessTokenForProvider(activeProvider);
};

export const useAuthOutsideContext = () => {
  return useStore(authStore);
};

export const useAuth = (): AuthContextValue => {
  const state = useStore(authStore);
  const clearIsSSO = useUserStore(store => store.clearIsSSO);

  const loginWithSupabase = async (email: string, password: string) => {
    const provider = getAuthProvider(AuthProviderType.SUPABASE);

    if (!provider.loginWithSupabase) {
      throw new Error('Supabase provider does not support password login.');
    }

    await provider.loginWithSupabase(email, password);
    state.setActiveProvider(AuthProviderType.SUPABASE);
  };

  const loginWithSupabaseSSO = async (providerId: string, redirectTo: string) => {
    state.setActiveProvider(AuthProviderType.SUPABASE);

    const provider = getAuthProvider(AuthProviderType.SUPABASE);

    if (!provider.loginWithSupabaseSSO) {
      throw new Error('Supabase provider does not support SSO login.');
    }

    return provider.loginWithSupabaseSSO(providerId, redirectTo);
  };

  const resolveSupabaseProviderForLoginEmail = (email: string): SupabaseProviderConfig => {
    supabaseProviderService.clearPersistedProvider();

    const domain = email.split('@')[1];

    const provider = supabaseProviderService.hasProviderByDomain(domain)
      ? supabaseProviderService.loadProviderByDomain(domain)
      : supabaseProviderService.loadDefaultProvider();

    supabaseProviderService.persistLoadedProvider();

    return provider;
  };

  const loginWithAuth0 = async (returnTo = '/') => {
    clearIsSSO();
    state.setActiveProvider(AuthProviderType.AUTH0);

    const provider = getAuthProvider(AuthProviderType.AUTH0);

    if (!provider.loginWithAuth0) {
      throw new Error('Auth0 provider does not support redirect login.');
    }

    await provider.loginWithAuth0(normalizeReturnPath(returnTo));
  };

  const logout = async () => {
    const currentProvider = state.activeProvider;

    clearPersistedUserAuthData();
    authStore.setState({
      session: null,
      activeProvider: null,
    });

    if (currentProvider) {
      const provider = getAuthProvider(currentProvider);
      await provider.logout();
      return;
    }

    try {
      await getAuthProvider(AuthProviderType.SUPABASE).logout();
    } catch {
      await getAuthProvider(AuthProviderType.AUTH0).logout();
    }
  };

  const getAccessToken = async () => {
    if (!state.activeProvider) {
      return null;
    }

    return getAccessTokenForProvider(state.activeProvider);
  };

  return {
    ...state,
    isAuthenticated: Boolean(state.session?.access_token),
    loginWithSupabase,
    loginWithSupabaseSSO,
    resolveSupabaseProviderForLoginEmail,
    loginWithAuth0,
    logout,
    getAccessToken,
  };
};

export const useUser = (): AuthUser | null => {
  const state = useAuth();

  return state.session?.user ?? null;
};
